import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalContext } from '../../../../Contexts/GlobalContext';
import axios from 'axios';
import { BASE_URL } from '../../../../Utlils';
import Required from '../../../../components/Required';
import { getValueByCountryRateAndLOI } from './helperData';
import SearchableCountrySelect from '../../../../components/SearchableCountrySelect';
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import RedirectInfo from './RedirectInfo';
import { HiInformationCircle } from 'react-icons/hi';

const Details = ({ data, getSingleSurvey, singleSurveyStats }) => {
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const {
    setSnackbar,
    teamMembers,
    studyTypesData,
    industryData,
    allLanguages,
    countryData,
  } = useGlobalContext();
  const [surveyData, setSurveyData] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  useEffect(() => {
    setSurveyData({ ...data });
    setSavedSnapshot(JSON.parse(JSON.stringify(data || {})));
  }, [Object.keys(data || {})?.length]);

  const changeCount = useMemo(() => {
    if (!savedSnapshot) return 0;
    let count = 0;
    const setupFields = [
      'studyType',
      'businessUnit',
      'industry',
      'liveUrl',
      'testUrl',
      'surveyCpi',
      'clientCpi',
      'country',
      'countryCode',
      'language',
      'industryLockout',
      'personalInformation',
      'targetAudience',
      'objective',
    ];
    for (const key of setupFields) {
      const a = String(surveyData?.setup?.[key] ?? '');
      const b = String(savedSnapshot?.setup?.[key] ?? '');
      if (a !== b) count++;
    }
    const matrixFields = [
      'expectedCompletes',
      'expectedIncidenceRate',
      'expectedCompletionLOI',
    ];
    for (const key of matrixFields) {
      const a = String(surveyData?.expectedMatrix?.[key] ?? '');
      const b = String(savedSnapshot?.expectedMatrix?.[key] ?? '');
      if (a !== b) count++;
    }
    const peopleFields = [
      'projectManager',
      'accountExecutive',
      'alternateProjectManager',
    ];
    for (const key of peopleFields) {
      const aRaw = surveyData?.peoples?.[key];
      const bRaw = savedSnapshot?.peoples?.[key];
      const a = String(aRaw?._id ?? aRaw ?? '');
      const b = String(bRaw?._id ?? bRaw ?? '');
      if (a !== b) count++;
    }
    return count;
  }, [surveyData, savedSnapshot]);
  // console.log(data);

  // Normalize URL by replacing various placeholder patterns with [%rid%]
  function normalizeUrl(url) {
    if (!url) return url;

    // List of patterns to replace with [%rid%]
    const patternsToReplace = [
      // Query parameter patterns (e.g., ?pid=xxx, ?rid=xxx, etc.)
      /([?&])(r|respID|respondentId|respondentID|pid|pID|pId|rid|RID|Rid)=([^&]*)/gi,
      // Common placeholder patterns in URL
      /xxxxx?/gi,
      /\[pid\]/gi,
      /\[%pid%\]/gi,
      /\[%%pid%%\]/gi,
      /\{pid\}/gi,
      /\{\{pid\}\}/gi,
      /\{rid\}/gi,
      /\{\{rid\}\}/gi,
      /\[rid\]/gi,
      /%rid%/gi,
      // Generic placeholder patterns
      /\[.*?\]/g, // Any [variable] pattern
      /\{.*?\}/g, // Any {variable} pattern
      /\{\{.*?\}\}/g, // Any {{variable}} pattern
      /%%\w+%%/g, // Any %%variable%% pattern
      /%\w+%/g, // Any %variable% pattern (but not [%rid%])
    ];

    let normalizedUrl = url;

    // First, handle query parameters (e.g., ?pid=value, ?rid=value)
    normalizedUrl = normalizedUrl.replace(
      /([?&])(r|respID|respondentId|respondentID|pid|pID|pId|rid|RID|Rid)=([^&]*)/gi,
      (match, separator, param, value) => {
        // Don't replace if it's already [%rid%]
        if (value.includes('[%rid%]')) {
          return match;
        }
        return `${separator}${param}=[%rid%]`;
      },
    );

    // Replace xxxx patterns in the URL
    normalizedUrl = normalizedUrl.replace(/xxxxx?/gi, '[%rid%]');

    // Replace common placeholder patterns (but preserve [%rid%] if it exists)
    if (!normalizedUrl.includes('[%rid%]')) {
      normalizedUrl = normalizedUrl
        .replace(/\[pid\]/gi, '[%rid%]')
        .replace(/\[%pid%\]/gi, '[%rid%]')
        .replace(/\[%%pid%%\]/gi, '[%rid%]')
        .replace(/\{pid\}/gi, '[%rid%]')
        .replace(/\{\{pid\}\}/gi, '[%rid%]')
        .replace(/\{rid\}/gi, '[%rid%]')
        .replace(/\{\{rid\}\}/gi, '[%rid%]')
        .replace(/\[rid\]/gi, '[%rid%]')
        .replace(/%rid%/gi, '[%rid%]')
        // Replace other placeholder patterns (but not if [%rid%] already exists)
        .replace(/\[(?!%rid%)[^\]]+\]/g, '[%rid%]')
        .replace(/\{(?!%rid%)[^}]+\}/g, '[%rid%]')
        .replace(/\{\{(?!%rid%)[^}]+\}\}/g, '[%rid%]');
    }

    return normalizedUrl;
  }

  // Validate URL has [%rid%]
  function validateUrlHasRid(url, fieldName) {
    if (!url) return { valid: true, error: null };

    const normalized = normalizeUrl(url);
    if (!normalized.includes('[%rid%]')) {
      return {
        valid: false,
        error: `${fieldName} must contain [%rid%] to pass respondent ID back.\n\nExample: https://example.com/survey?rid=[%rid%]`,
      };
    }
    return { valid: true, error: null };
  }

  // Launch Readiness Checklist Component
  const LaunchReadinessChecklist = ({ surveyData }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const checklistItems = useMemo(() => {
      const items = [];

      // Setup Requirements
      items.push({
        category: 'Setup Requirements',
        checks: [
          {
            label: 'Study Type',
            isValid: !!surveyData?.setup?.studyType,
            value: surveyData?.setup?.studyType || 'Not set',
          },
          {
            label: 'Industry',
            isValid: !!surveyData?.setup?.industry,
            value: surveyData?.setup?.industry || 'Not set',
          },
          {
            label: 'Country',
            isValid: !!surveyData?.setup?.country,
            value: surveyData?.setup?.country || 'Not set',
          },
          {
            label: 'Language',
            isValid: !!surveyData?.setup?.language,
            value: surveyData?.setup?.language || 'Not set',
          },
          {
            label: 'Target Audience',
            isValid: !!surveyData?.setup?.targetAudience,
            value: surveyData?.setup?.targetAudience || 'Not set',
          },
          {
            label: 'Survey CPI',
            isValid:
              !!surveyData?.setup?.surveyCpi &&
              Number(surveyData?.setup?.surveyCpi) > 0,
            value: surveyData?.setup?.surveyCpi
              ? `$${surveyData.setup.surveyCpi}`
              : 'Not set',
          },
        ],
      });

      // Survey Links
      const liveUrlValidation = validateUrlHasRid(
        surveyData?.setup?.liveUrl,
        'Live URL',
      );
      items.push({
        category: 'Survey Links',
        checks: [
          {
            label: 'Live URL',
            isValid: !!surveyData?.setup?.liveUrl && liveUrlValidation.valid,
            value: surveyData?.setup?.liveUrl ? 'Set' : 'Not set',
            warning:
              surveyData?.setup?.liveUrl && !liveUrlValidation.valid
                ? 'URL must contain [%rid%]'
                : null,
          },
          {
            label: 'Test URL (Recommended)',
            isValid: !!surveyData?.setup?.testUrl,
            value: surveyData?.setup?.testUrl ? 'Set' : 'Not set',
            optional: true,
          },
        ],
      });

      // Expected Metrics
      items.push({
        category: 'Expected Metrics',
        checks: [
          {
            label: 'Expected Completes',
            isValid:
              !!surveyData?.expectedMatrix?.expectedCompletes &&
              Number(surveyData?.expectedMatrix?.expectedCompletes) > 0,
            value: surveyData?.expectedMatrix?.expectedCompletes || 'Not set',
          },
          {
            label: 'Expected Completion LOI (minutes)',
            isValid:
              !!surveyData?.expectedMatrix?.expectedCompletionLOI &&
              Number(surveyData?.expectedMatrix?.expectedCompletionLOI) > 0,
            value: surveyData?.expectedMatrix?.expectedCompletionLOI
              ? `${surveyData.expectedMatrix.expectedCompletionLOI} min`
              : 'Not set',
          },
          {
            label: 'Expected Incidence Rate (%)',
            isValid:
              !!surveyData?.expectedMatrix?.expectedIncidenceRate &&
              Number(surveyData?.expectedMatrix?.expectedIncidenceRate) > 0,
            value: surveyData?.expectedMatrix?.expectedIncidenceRate
              ? `${surveyData.expectedMatrix.expectedIncidenceRate}%`
              : 'Not set',
          },
        ],
      });

      // Team & Qualifications
      const hasProjectManager = Array.isArray(
        surveyData?.peoples?.projectManager,
      )
        ? surveyData?.peoples?.projectManager.length > 0
        : !!surveyData?.peoples?.projectManager;

      items.push({
        category: 'Team & Qualifications',
        checks: [
          {
            label: 'Project Manager',
            isValid: hasProjectManager,
            value: hasProjectManager ? 'Assigned' : 'Not assigned',
          },
          {
            label: 'Qualifications',
            isValid:
              !!surveyData?.qualification &&
              surveyData?.qualification.length > 0,
            value:
              surveyData?.qualification?.length > 0
                ? `${surveyData.qualification.length} question(s)`
                : 'Not set',
          },
        ],
      });

      // Allocation
      const hasAllocation =
        surveyData?.allocation &&
        surveyData.allocation.length > 0 &&
        surveyData.allocation[0]?.allocation?.number > 0;
      items.push({
        category: 'Allocation',
        checks: [
          {
            label: 'Supplier Allocation',
            isValid: hasAllocation,
            value: hasAllocation
              ? `${surveyData.allocation[0].allocation.number} completes`
              : 'Not configured',
          },
        ],
      });

      return items;
    }, [surveyData]);

    const totalChecks = checklistItems.reduce(
      (sum, item) => sum + item.checks.length,
      0,
    );
    const completedChecks = checklistItems.reduce(
      (sum, item) =>
        sum +
        item.checks.filter((check) => check.isValid || check.optional).length,
      0,
    );
    const requiredChecks = checklistItems.reduce(
      (sum, item) =>
        sum +
        item.checks.filter((check) => check.isValid && !check.optional).length,
      0,
    );
    const requiredTotal = checklistItems.reduce(
      (sum, item) =>
        sum + item.checks.filter((check) => !check.optional).length,
      0,
    );
    const isReady = requiredChecks === requiredTotal;

    return (
      <div className="w-full bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
        {/* Header - Clickable to toggle */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-6 py-4 border-b border-[#E0E0E0] bg-gradient-to-r from-blue-50/30 to-white hover:from-blue-50/50 hover:to-white transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {isExpanded ? (
                <FaChevronUp
                  className="text-[#0226BE] flex-shrink-0"
                  size={16}
                />
              ) : (
                <FaChevronDown
                  className="text-[#0226BE] flex-shrink-0"
                  size={16}
                />
              )}
              <div className="flex-1 text-left">
                <h2 className="text-[#0226BE] text-[18px] font-[600] mb-1">
                  Launch Readiness Checklist
                </h2>
                <p className="text-[#666] text-[13px] font-[400]">
                  Ensure all required fields are configured before launching
                  your survey
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-[#333] text-[20px] font-[700]">
                  {requiredChecks}/{requiredTotal}
                </p>
                <p className="text-[#666] text-[12px] font-[400]">Required</p>
              </div>
              <div
                className={`px-4 py-2 rounded-md border ${
                  isReady
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-orange-50 border-orange-200 text-orange-700'
                }`}
              >
                <p className="text-[13px] font-[600]">
                  {isReady ? 'Ready to Launch' : 'Not Ready'}
                </p>
              </div>
            </div>
          </div>
        </button>

        {/* Checklist Items - Collapsible */}
        {isExpanded && (
          <>
            <div className="p-6 space-y-6">
              {checklistItems.map((category, categoryIndex) => (
                <div key={categoryIndex} className="space-y-3">
                  <h3 className="text-[#333] text-[14px] font-[600] uppercase tracking-wide">
                    {category.category}
                  </h3>
                  <div className="space-y-2">
                    {category.checks.map((check, checkIndex) => (
                      <div
                        key={checkIndex}
                        className={`flex items-center justify-between p-3 rounded-md border transition-colors ${
                          check.isValid
                            ? 'bg-green-50/30 border-green-200'
                            : check.optional
                              ? 'bg-gray-50 border-gray-200'
                              : 'bg-red-50/30 border-red-200'
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {check.isValid ? (
                            <FaCheckCircle
                              className="text-green-600 flex-shrink-0"
                              size={18}
                            />
                          ) : check.optional ? (
                            <FaExclamationCircle
                              className="text-gray-400 flex-shrink-0"
                              size={18}
                            />
                          ) : (
                            <FaExclamationTriangle
                              className="text-red-500 flex-shrink-0"
                              size={18}
                            />
                          )}
                          <div className="flex-1">
                            <p
                              className={`text-[14px] font-[500] ${
                                check.isValid
                                  ? 'text-[#333]'
                                  : check.optional
                                    ? 'text-[#666]'
                                    : 'text-red-700'
                              }`}
                            >
                              {check.label}
                              {check.optional && (
                                <span className="text-[12px] text-gray-500 ml-1">
                                  (Optional)
                                </span>
                              )}
                            </p>
                            {check.warning && (
                              <p className="text-[12px] text-red-600 mt-1">
                                {check.warning}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className={`text-[13px] font-[400] ${
                              check.isValid
                                ? 'text-green-700'
                                : check.optional
                                  ? 'text-[#666]'
                                  : 'text-red-600'
                            }`}
                          >
                            {check.value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer Message */}
            {!isReady && (
              <div className="px-6 py-4 bg-orange-50 border-t border-orange-200">
                <p className="text-[13px] text-orange-800">
                  <strong>Action Required:</strong> Please complete all required
                  fields marked with{' '}
                  <FaExclamationTriangle
                    className="inline text-red-500"
                    size={14}
                  />{' '}
                  before launching your survey.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };
  // console.log(surveyData?.allocation?.[0]?.allocation?.number);
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate Live URL has [%rid%]
    if (surveyData?.setup?.liveUrl) {
      const validation = validateUrlHasRid(
        surveyData.setup.liveUrl,
        'Live URL',
      );
      if (!validation.valid) {
        setSnackbar({
          msg: validation.error,
          open: true,
          severity: 'error',
        });
        return;
      }
    }

    setLoading(true);

    // Normalize URLs before submitting
    const normalizedLiveUrl = surveyData?.setup?.liveUrl
      ? normalizeUrl(surveyData.setup.liveUrl)
      : '';
    const normalizedTestUrl = surveyData?.setup?.testUrl
      ? normalizeUrl(surveyData.setup.testUrl)
      : '';

    await axios
      .put(`${BASE_URL}/survey?id=${data?._id}`, {
        ...surveyData,
        setup: {
          ...surveyData?.setup,
          liveUrl: normalizedLiveUrl,
          testUrl: normalizedTestUrl,
        },
        allocation: [
          {
            ...surveyData?.allocation?.[0],
            allocation: {
              number:
                surveyData?.expectedMatrix?.expectedCompletes ||
                surveyData?.allocation?.[0]?.allocation?.number,
              percentage: 100,
            },
            cpi: Number(surveyData?.setup?.surveyCpi),
          },
        ],
      })
      .then((res) => {
        setLoading(false);
        const savedState = {
          ...surveyData,
          setup: {
            ...surveyData?.setup,
            liveUrl: normalizedLiveUrl,
            testUrl: normalizedTestUrl,
          },
          expectedMatrix: surveyData?.expectedMatrix,
          peoples: surveyData?.peoples,
        };
        setSavedSnapshot(JSON.parse(JSON.stringify(savedState)));
        getSingleSurvey();
        setSnackbar((prev) => ({
          ...prev,
          open: true,
          msg: 'Survey Details Updated !',
          severity: 'success',
        }));
      })
      .catch((er) => {
        setLoading(false);
        setSnackbar({
          msg: 'Something went wrong !',
          open: true,
          severity: 'danger',
        });
      });
  };
  const renderComponent = (e) => {
    switch (tab) {
      case 0:
        return (
          <>
            <SetupRequirement
              surveyData={surveyData}
              setSurveyData={setSurveyData}
              countryData={countryData}
              loading={loading}
              studyTypesData={studyTypesData}
              industryData={industryData}
              allLanguages={allLanguages}
              setSnackbar={setSnackbar}
              singleSurveyStats={singleSurveyStats}
            />
            <ExpectedMatrix
              surveyData={surveyData}
              setSurveyData={setSurveyData}
              loading={loading}
              setSnackbar={setSnackbar}
            />
            <Peoples
              surveyData={surveyData}
              setSurveyData={setSurveyData}
              teamMembers={teamMembers}
            />
          </>
        );
      case 1:
        return (
          <ProjectSettings
            loading={loading}
            surveyData={surveyData}
            setSurveyData={setSurveyData}
            getSingleSurvey={getSingleSurvey}
            setSnackbar={setSnackbar}
          />
        );
      default:
        return <h1>No Page</h1>;
    }
  };
  // console.log("singleSurveyStats=>", singleSurveyStats);
  // console.log("surveyData=>", surveyData);
  // console.log("countryData=>", countryData);

  return (
    <div className="enterprise-details-page flex flex-col w-full gap-6">
      {/* Launch Readiness Checklist - Always visible at top */}
      <LaunchReadinessChecklist surveyData={surveyData} />

      <div className="flex flex-col gap-6">
        {/* Navigation Tabs - Only Setup and Project Settings */}
        <div className="w-full bg-white border border-[#E0E0E0] rounded-xl overflow-hidden shadow-sm">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E0E0E0] bg-[#F8FAFC]">
            <button
              type="button"
              className={`text-[14px] font-[600] cursor-pointer transition-all px-4 py-2 rounded-md border ${
                tab === 0
                  ? `text-[#0226BE] bg-white border-[#BFD1FF] shadow-sm`
                  : 'text-[#475467] border-transparent hover:text-[#0226BE] hover:bg-white/80'
              }`}
              onClick={(e) => {
                setTab(0);
              }}
            >
              Setup & Configuration
            </button>
            <button
              type="button"
              className={`text-[14px] font-[600] cursor-pointer transition-all px-4 py-2 rounded-md border ${
                tab === 1
                  ? `text-[#0226BE] bg-white border-[#BFD1FF] shadow-sm`
                  : 'text-[#475467] border-transparent hover:text-[#0226BE] hover:bg-white/80'
              }`}
              onClick={(e) => {
                setTab(1);
              }}
            >
              Project Settings
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6 pb-24">
          {renderComponent()}
          <div className="sticky bottom-4 z-20">
            <div className="w-full bg-white/95 backdrop-blur border border-[#D0D5DD] rounded-xl px-5 py-3 shadow-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                {changeCount > 0 ? (
                  <>
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0226BE] text-white text-[12px] font-[700]">
                      {changeCount}
                    </span>
                    <span className="text-[14px] font-[500] text-[#344054]">
                      unsaved {changeCount === 1 ? 'change' : 'changes'}
                    </span>
                  </>
                ) : (
                  <span className="text-[14px] font-[400] text-[#667085]">
                    No changes
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={loading || changeCount === 0}
                className={`px-6 py-2.5 rounded-md font-[600] text-[14px] transition-all flex items-center gap-2 ${
                  loading || changeCount === 0
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-[#0226BE] text-white hover:bg-[#021ea8]'
                }`}
              >
                {loading ? (
                  <>
                    <div
                      className="enterprise-loader enterprise-loader--sm enterprise-loader--on-dark"
                      aria-hidden="true"
                    />
                    <span>Saving...</span>
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
const SetupRequirement = ({
  surveyData,
  setSurveyData,
  loading,
  industryData,
  studyTypesData,
  allLanguages,
  setSnackbar,
  countryData,
  singleSurveyStats,
}) => {
  // Normalize URL by replacing various placeholder patterns with [%rid%]
  const normalizeUrl = (url) => {
    if (!url) return url;

    // First, handle query parameters (e.g., ?pid=value, ?rid=value)
    let normalized = url.replace(
      /([?&])(r|respID|respondentId|respondentID|pid|pID|pId|rid|RID|Rid)=([^&]*)/gi,
      (match, separator, param, value) => {
        // Don't replace if it's already [%rid%]
        if (value.includes('[%rid%]')) {
          return match;
        }
        return `${separator}${param}=[%rid%]`;
      },
    );

    // Replace xxxx patterns in the URL
    normalized = normalized.replace(/xxxxx?/gi, '[%rid%]');

    // Only replace placeholder patterns if [%rid%] doesn't already exist
    if (!normalized.includes('[%rid%]')) {
      normalized = normalized
        .replace(/\[pid\]/gi, '[%rid%]')
        .replace(/\[%pid%\]/gi, '[%rid%]')
        .replace(/\[%%pid%%\]/gi, '[%rid%]')
        .replace(/\{pid\}/gi, '[%rid%]')
        .replace(/\{\{pid\}\}/gi, '[%rid%]')
        .replace(/\{rid\}/gi, '[%rid%]')
        .replace(/\{\{rid\}\}/gi, '[%rid%]')
        .replace(/\[rid\]/gi, '[%rid%]')
        // Replace standalone %rid% (but not [%rid%])
        .replace(/(^|[^[])%rid%([^]]|$)/gi, '$1[%rid%]$2')
        // Replace other placeholder patterns
        .replace(/\[(?!%rid%)[^\]]+\]/g, '[%rid%]')
        .replace(/\{(?!%rid%)[^}]+\}/g, '[%rid%]')
        .replace(/\{\{(?!%rid%)[^}]+\}\}/g, '[%rid%]');
    }

    return normalized;
  };

  // Validate URL has [%rid%]
  const validateUrlHasRid = (url, fieldName) => {
    if (!url) return { valid: true, error: null };

    const normalized = normalizeUrl(url);
    if (!normalized.includes('[%rid%]')) {
      return {
        valid: false,
        error: `${fieldName} must contain [%rid%] to pass respondent ID back.\n\nExample: https://example.com/survey?rid=[%rid%]`,
      };
    }
    return { valid: true, error: null };
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    // Auto-normalize URLs on change for liveUrl and testUrl
    if (name === 'liveUrl' || name === 'testUrl') {
      value = normalizeUrl(value);
    }

    // console.log(name, value);
    setSurveyData((prev) => ({
      ...prev,
      setup: {
        ...prev?.setup,
        [name]: value,
      },
    }));
  };

  // Handle blur validation for URL fields
  const handleUrlBlur = (e, fieldName) => {
    const value = e.target.value;
    if (!value) return;

    const validation = validateUrlHasRid(value, fieldName);
    if (!validation.valid) {
      setSnackbar({
        msg: validation.error,
        open: true,
        severity: 'error',
      });
    }
  };
  const dynamicCPISuggestion =
    (10 / 100) * surveyData?.expectedMatrix?.expectedCompletes <
    singleSurveyStats?.completed;

  let CPIValue = useMemo(
    () =>
      dynamicCPISuggestion
        ? getValueByCountryRateAndLOI(
            surveyData?.setup?.country,
            Math.ceil(Number(singleSurveyStats?.incidenceRate)),
            Math.ceil(Number(singleSurveyStats?.avgCompletionLoi)),
            surveyData?.setup?.targetAudience || 'B2C',
          )
        : getValueByCountryRateAndLOI(
            surveyData?.setup?.country,
            surveyData?.expectedMatrix?.expectedIncidenceRate,
            surveyData?.expectedMatrix?.expectedCompletionLOI,
            surveyData?.setup?.targetAudience,
          ),
    [
      dynamicCPISuggestion,
      surveyData?.setup?.country,
      surveyData?.expectedMatrix?.expectedIncidenceRate,
      surveyData?.expectedMatrix?.expectedCompletionLOI,
      surveyData?.setup?.targetAudience,
      surveyData?.setup?.surveyCpi,
    ],
  );
  const getRandomNoticeLine = useCallback((value) => {
    let arr = [
      `For optimal conversion rates and audience engagement, ensure that your CPI (Country, LOI, IR) value meets or exceeds $${value}`,
      `To achieve better conversion and reach your target audience effectively, maintain a minimum CPI value of $${value} based on your selected Country, LOI, and IR.`,
      `Boost your conversion success and audience interaction by aiming for a CPI value of at least $${value} considering your Country, LOI, and IR parameters.`,
      `A CPI value of $${value} or higher is recommended to secure strong conversion rates and effectively engage your audience, tailored to your Country, LOI, and IR metrics.`,
    ];
    return arr[Math.floor(Math.random() * arr.length)];
  }, []);
  // console.log("CPIValue=>", CPIValue);
  // console.log("surveyData=>", surveyData);
  // console.log(
  //   getValueByCountryRateAndLOI(
  //     surveyData?.setup?.country,
  //     surveyData?.expectedMatrix?.expectedIncidenceRate,
  //     surveyData?.expectedMatrix?.expectedCompletionLOI,
  //     surveyData?.setup?.targetAudience
  //   )
  // );
  return (
    <div className="w-full">
      <div className="w-full tableBorder enterprise-section-header py-4 px-6">
        <h1 className="text-[#0F172A] text-[20px] font-[700] tracking-tight">
          Setup Requirement
        </h1>
        <p className="text-[#475467] text-[14px] font-[400] mt-1">
          Complete this information to set your survey live.
        </p>
      </div>
      <div className="w-full tableBorder enterprise-section-body pt-6 pb-8 flex items-center justify-center">
        <div className="w-full px-10 flex flex-col gap-[28px]">
          <div className="flex flex-col gap-[16px]">
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Study Type
                  <Required />
                </label>
                <select
                  name="studyType"
                  id="studyType"
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-10 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 appearance-none cursor-pointer hover:border-gray-400"
                  required
                  onChange={handleChange}
                  value={surveyData?.setup?.studyType}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '12px',
                  }}
                >
                  <option value="">Select Study Type</option>
                  {studyTypesData?.map((data, ind) => (
                    <option value={data?.value} key={ind}>
                      {data?.label}
                    </option>
                  ))}
                </select>
                {/* <input
                  type="text"
                  // placeholder="Abcd"
                  name="studyType"
                  required
                  onChange={handleChange}
                  value={surveyData?.setup?.studyType}
                  className="w-full border border-[#E0E0E0] bg-[#FAFAFA] rounded-[4px] pl-3 text-[#12112766] font-[400] text-[14px] h-[40px]"
                /> */}
              </div>
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px]">
                  Business Unit
                </label>
                <input
                  type="text"
                  name="businessUnit"
                  required
                  readOnly
                  value={surveyData?.setup?.businessUnit}
                  className="w-full border border-gray-300 bg-gray-50 rounded-md pl-4 pr-4 py-3 text-[#666] font-[400] text-[14px] h-[48px] cursor-not-allowed focus:outline-none transition-all"
                />
              </div>
            </div>
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Industry <Required />
                </label>
                <select
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-10 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 appearance-none cursor-pointer hover:border-gray-400"
                  name="industry"
                  required
                  onChange={handleChange}
                  value={surveyData?.setup?.industry}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '12px',
                  }}
                >
                  <option value="">Select industry</option>
                  {industryData?.map((data, key) => (
                    <option key={key} value={data?.value}>
                      {data?.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Live URL <Required />
                </label>
                <input
                  type="url"
                  required
                  placeholder="https://example.com/survey?rid=[%rid%]"
                  name="liveUrl"
                  onChange={handleChange}
                  onBlur={(e) => handleUrlBlur(e, 'Live URL')}
                  value={surveyData?.setup?.liveUrl}
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-4 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-400"
                />
              </div>
            </div>
            <div className="grid grid-cols-2  gap-[30px] ">
              <div className="w-full flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px]">
                  Test URL
                </label>
                <input
                  type="url"
                  placeholder="https://example.com/survey?rid=test"
                  name="testUrl"
                  onChange={handleChange}
                  onBlur={(e) => handleUrlBlur(e, 'Test URL')}
                  value={surveyData?.setup?.testUrl}
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-4 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-400"
                />
              </div>
              <div className="w-full flex flex-col gap-2">
                <div className="flex items-center gap-1 flex-wrap">
                  <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                    Survey CPI (USD) <Required />
                  </label>
                  <span className="text-[#0226BE] text-[12px] font-[400]">
                    {isNaN(Number(CPIValue))
                      ? CPIValue?.includes('Contact support')
                        ? CPIValue
                        : ''
                      : Number(
                            surveyData?.setup?.personalInformation
                              ? parseFloat((Number(CPIValue) + 2)?.toFixed(2))
                              : CPIValue,
                          ) > Number(surveyData?.setup?.surveyCpi)
                        ? getRandomNoticeLine(
                            surveyData?.setup?.personalInformation
                              ? parseFloat((Number(CPIValue) + 2)?.toFixed(2))
                              : CPIValue,
                          )
                        : ''}
                  </span>
                </div>
                <input
                  type="number"
                  placeholder={
                    surveyData?.setup?.targetAudience === 'B2B'
                      ? '5.00'
                      : '1.00'
                  }
                  name="surveyCpi"
                  step="0.01"
                  min={surveyData?.setup?.targetAudience === 'B2B' ? '5' : '1'}
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const minCPI =
                      surveyData?.setup?.targetAudience === 'B2B' ? 5 : 1;
                    const value = Number(e.target.value);

                    if (value < minCPI && value > 0) {
                      setSnackbar({
                        msg: `Survey CPI should be at least $${minCPI} USD for ${surveyData?.setup?.targetAudience === 'B2B' ? 'B2B' : 'B2C'} surveys`,
                        open: true,
                        severity: 'error',
                      });
                      return;
                    }

                    if (value <= 499) {
                      handleChange(e);
                    } else {
                      setSnackbar({
                        msg: 'Survey CPI should be less than or equal to 499',
                        open: true,
                        severity: 'error',
                      });
                    }
                  }}
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-4 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-400"
                  required
                  value={surveyData?.setup?.surveyCpi}
                />
              </div>
            </div>
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px]">
                  Client CPI (USD)
                </label>
                <input
                  type="number"
                  placeholder="$ USD"
                  name="clientCpi"
                  step="0.01"
                  min="0.50"
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    if (e.target.value <= 499) {
                      handleChange(e);
                    } else {
                      setSnackbar({
                        msg: 'Client CPI should be less than or equal to 499',
                        open: true,
                        severity: 'error',
                      });
                    }
                  }}
                  value={surveyData?.setup?.clientCpi}
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-4 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-400"
                />
              </div>
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Country <Required />
                </label>
                <SearchableCountrySelect
                  countryData={countryData}
                  value={surveyData?.setup?.countryCode}
                  onChange={(selectedOption) => {
                    setSurveyData((prev) => ({
                      ...prev,
                      setup: {
                        ...prev?.setup,
                        country: selectedOption?.label || '',
                        countryCode: selectedOption?.value || '',
                      },
                    }));
                  }}
                  placeholder="Select Country"
                  isRequired={true}
                  height="48px"
                />
              </div>
            </div>
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-[8px]">
                <p className="text-[#333] font-[500] text-[14px] ">
                  Industry Lockout
                </p>
                <select
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-10 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 appearance-none cursor-pointer hover:border-gray-400"
                  name="industryLockout"
                  onChange={handleChange}
                  required
                  value={surveyData?.setup?.industryLockout}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '12px',
                  }}
                >
                  <option selected disabled>
                    Select Lockout
                  </option>
                  <option value="7 Days">Past 7 Days / 1 Week</option>
                  <option value="14 Days">Past 14 Days / 2 Week</option>
                  <option value="21 Days">Past 21 Days / 3 Week</option>
                  <option value="28 Days">Past 28 Days / 4 Week</option>
                </select>
              </div>
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Language <Required />
                </label>
                <select
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-10 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 appearance-none cursor-pointer hover:border-gray-400"
                  name="language"
                  required
                  onChange={handleChange}
                  value={surveyData?.setup?.language}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '12px',
                  }}
                >
                  <option selected disabled>
                    Select language{' '}
                  </option>
                  {allLanguages?.map((data, key) => (
                    <option value={data} key={key}>
                      {data}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-[8px]">
                <p className="text-[#333] font-[500] text-[14px]">
                  Does your survey collect personal information that can be used
                  to identify an individual ? <Required />
                </p>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      id="personalInfo"
                      name="personalInformation"
                      onChange={(e) =>
                        setSurveyData((prev) => ({
                          ...prev,
                          setup: {
                            ...prev?.setup,
                            personalInformation: true,
                          },
                        }))
                      }
                      checked={surveyData?.setup?.personalInformation}
                      className="w-4 h-4 border-2 border-gray-300 text-[#0226BE] focus:ring-2 focus:ring-[#0226BE] focus:ring-offset-1 cursor-pointer transition-all"
                    />
                    <span className="text-[14px] text-[#333] font-[400] group-hover:text-[#0226BE] transition-colors">
                      Yes
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      name="personalInformation"
                      id="personalInfo2"
                      onChange={(e) =>
                        setSurveyData((prev) => ({
                          ...prev,
                          setup: {
                            ...prev?.setup,
                            personalInformation: false,
                          },
                        }))
                      }
                      checked={surveyData?.setup?.personalInformation === false}
                      className="w-4 h-4 border-2 border-gray-300 text-[#0226BE] focus:ring-2 focus:ring-[#0226BE] focus:ring-offset-1 cursor-pointer transition-all"
                    />
                    <span className="text-[14px] text-[#333] font-[400] group-hover:text-[#0226BE] transition-colors">
                      No
                    </span>
                  </label>
                </div>
              </div>
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Target Audience <Required />
                </label>
                <select
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-10 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 appearance-none cursor-pointer hover:border-gray-400"
                  name="targetAudience"
                  required
                  onChange={(e) =>
                    setSurveyData((prev) => ({
                      ...prev,
                      setup: { ...prev?.setup, targetAudience: e.target.value },
                    }))
                  }
                  value={surveyData?.setup?.targetAudience}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '12px',
                  }}
                >
                  <option selected disabled>
                    Select Target Audience{' '}
                  </option>
                  {['B2B', 'B2C', 'Healthcare']?.map((data, i) => (
                    <option value={data} key={i}>
                      {data}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="w-full flex flex-col gap-2">
              <label className="text-[#1a1a1a] font-[500] text-[14px]">
                Objective (Target audience, exclusions, and key screening needs)
              </label>
              <textarea
                name="objective"
                rows={5}
                placeholder="Describe target audience, exclusions, and any other qualification requirements. This will be used for AI qualification creation."
                onChange={handleChange}
                value={surveyData?.setup?.objective || ''}
                className="w-full border border-gray-300 bg-white rounded-md px-4 py-3 text-[#1a1a1a] font-[400] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-400 resize-y min-h-[120px]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const ExpectedMatrix = ({
  surveyData,
  setSurveyData,
  loading,
  setSnackbar,
}) => {
  const handleChange = (e) => {
    let { name, value } = e.target;
    setSurveyData((prev) => ({
      ...prev,
      expectedMatrix: {
        ...prev?.expectedMatrix,
        [name]: value,
      },
    }));
  };
  // console.log(surveyData);
  return (
    <div className="w-full">
      <div className="w-full tableBorder enterprise-section-header py-4 px-6">
        <h1 className="text-[#0F172A] text-[20px] font-[700] tracking-tight">
          Expected Metrics & Data
        </h1>
        <p className="text-[#475467] text-[14px] font-[400] mt-1">
          Fill in to inform suppliers of your survey before receiving traffic
          and review in-field data when survey is live.
        </p>
      </div>
      <div className="w-full tableBorder enterprise-section-body pt-6 pb-8 flex items-center justify-center">
        <div className="w-full px-10 flex flex-col gap-[28px]">
          <div className="flex flex-col gap-[16px]">
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Expected Completes
                  <Required />
                </label>
                <input
                  type="number"
                  // placeholder="293"
                  min="10"
                  name="expectedCompletes"
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  required
                  value={surveyData?.expectedMatrix?.expectedCompletes}
                  onChange={(e) => {
                    if (e.target.value <= 1000000) {
                      handleChange(e);
                    } else {
                      setSnackbar({
                        open: true,
                        msg: 'Expected Completes should be less than or equal to 1000000',
                        severity: 'error',
                      });
                    }
                  }}
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-4 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-400"
                />
              </div>
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Expected Incidence Rate
                  <Required />
                </label>
                <input
                  type="number"
                  name="expectedIncidenceRate"
                  step="0.01"
                  required
                  min="1"
                  value={surveyData?.expectedMatrix?.expectedIncidenceRate}
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    if (e.target.value <= 100) {
                      handleChange(e);
                    } else {
                      setSnackbar({
                        open: true,
                        msg: 'Expected IR should be less than or equal to 100 %',
                        severity: 'error',
                      });
                    }
                  }}
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-4 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-400"
                />
              </div>
            </div>
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Expected Completion LOI <Required />
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  min="1"
                  name="expectedCompletionLOI"
                  value={
                    surveyData?.expectedMatrix?.expectedCompletionLOI || ''
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    if (e.target.value <= 90) {
                      handleChange(e);
                    } else {
                      setSnackbar({
                        open: true,
                        msg: 'Expected LOI should be less than or equal to 90 mins',
                        severity: 'error',
                      });
                    }
                  }}
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-4 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 placeholder:text-gray-400 hover:border-gray-400"
                />
              </div>
              <div className="w-2/4 "></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const Peoples = ({ surveyData, setSurveyData, teamMembers }) => {
  const handleChange = (e) => {
    let { name, value } = e.target;
    setSurveyData((prev) => ({
      ...prev,
      peoples: {
        ...prev?.peoples,
        [name]: value,
      },
    }));
  };
  // console.log(surveyData);
  // console.log(teamMembers);
  return (
    <div className="w-full">
      <div className="w-full tableBorder enterprise-section-header py-4 px-6">
        <h1 className="text-[#0F172A] text-[20px] font-[700] tracking-tight">
          People & Ownership
        </h1>
        <p className="text-[#475467] text-[14px] font-[400] mt-1">
          Manage ownership of the survey for reporting and communication.
        </p>
      </div>
      <div className="w-full tableBorder enterprise-section-body pt-6 pb-8 flex items-center justify-center">
        <div className="w-full px-10 flex flex-col gap-[28px]">
          <div className="flex flex-col gap-[16px]">
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px] flex items-center gap-1">
                  Project Manager <Required />
                </label>
                <select
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-10 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 appearance-none cursor-pointer hover:border-gray-400"
                  name="projectManager"
                  onChange={handleChange}
                  required
                  value={
                    surveyData?.peoples?.projectManager?._id
                      ? surveyData?.peoples?.projectManager?._id
                      : surveyData?.peoples?.projectManager
                  }
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '12px',
                  }}
                >
                  <option value="">Select Project Manager</option>
                  {teamMembers?.map((data, key) => (
                    <option value={data?._id} key={key}>
                      {data?.personalDetails?.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px]">
                  Account Executive
                </label>
                <select
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-10 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 appearance-none cursor-pointer hover:border-gray-400"
                  name="accountExecutive"
                  onChange={handleChange}
                  value={
                    surveyData?.peoples?.accountExecutive?._id
                      ? surveyData?.peoples?.accountExecutive?._id
                      : surveyData?.peoples?.accountExecutive
                  }
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '12px',
                  }}
                >
                  <option value="">Select Account Executive</option>
                  {teamMembers?.map((data, key) => (
                    <option value={data?._id} key={key}>
                      {data?.personalDetails?.fullName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="details-row flex items-center justify-between gap-[30px]">
              <div className="w-2/4 flex flex-col gap-2">
                <label className="text-[#1a1a1a] font-[500] text-[14px]">
                  Expected Alternate Project Manager
                </label>
                <select
                  className="w-full border border-gray-300 bg-white rounded-md pl-4 pr-10 py-3 text-[#1a1a1a] font-[400] text-[14px] h-[48px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 appearance-none cursor-pointer hover:border-gray-400"
                  name="alternateProjectManager"
                  onChange={handleChange}
                  value={
                    surveyData?.peoples?.alternateProjectManager?._id
                      ? surveyData?.peoples?.alternateProjectManager?._id
                      : surveyData?.peoples?.alternateProjectManager
                  }
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '12px',
                  }}
                >
                  <option value="">Select Alternate Project Manager</option>
                  {teamMembers?.map((data, key) => (
                    <option value={data?._id} key={key}>
                      {data?.personalDetails?.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-2/4 flex flex-col gap-[8px]"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const ProjectSettings = ({
  loading,
  surveyData,
  setSurveyData,
  getSingleSurvey,
  setSnackbar,
}) => {
  const { user } = useGlobalContext();
  const [redirectInfoModal, setRedirectInfoModal] = useState(false);
  // console.log("surveyData=>", surveyData);
  const handleChange = async (e) => {
    await axios
      .put(`${BASE_URL}/survey?id=${surveyData?._id}`, {
        objectType: Number(e.target.value),
      })
      .then(() => {
        getSingleSurvey();
        setSnackbar((prev) => ({
          ...prev,
          open: true,
          msg: 'Survey Details Updated !',
          severity: 'success',
        }));
      })
      .catch((er) => {
        setSnackbar({
          msg: 'Something went wrong !',
          open: true,
          severity: 'danger',
        });
      });
  };
  return (
    <div>
      <div className="w-full tableBorder enterprise-section-header py-4 px-6 flex items-center justify-between">
        <h1 className="text-[#0F172A] text-[20px] font-[700] tracking-tight">
          Project Settings
        </h1>
        <button
          onClick={() => setRedirectInfoModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-[#0226BE] rounded-md transition-colors text-[14px] font-[500] border border-blue-200"
        >
          <HiInformationCircle size={18} />
          Redirect Info
        </button>
      </div>
      <div className="w-full tableBorder enterprise-section-body pt-6 pb-8 flex items-center justify-center">
        <div className="w-full px-10   flex flex-col gap-[28px]">
          <p className="text-[#333] text-[16px] font-[500]">
            Group survey for improved security and financial reporting benefits.
          </p>
          <div className="details-row flex items-start gap-[30px] justify-between">
            <div>
              <p className="text-[#333] text-[14px] font-[500]">Client</p>
              <h1 className="text-[#333] text-[20px] font-[600]">
                {user?.organizationName || '_'}
              </h1>
            </div>
            <div className="w-2/4 flex flex-col gap-[8px]">
              <p className="text-[#333] font-[500] text-[14px]">Object Type</p>
              <select
                className="w-full border border-[#E0E0E0] bg-white rounded-[4px] pl-3 pr-3 text-myBlack font-[400] text-[14px] h-[44px] focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-transparent transition-all"
                name="accountExecutive"
                onChange={(e) => {
                  setSurveyData((prev) => ({
                    ...prev,
                    objectType: Number(e.target.value),
                  }));
                  handleChange(e);
                }}
                value={surveyData?.objectType}
              >
                <option value="">Select Object Type</option>
                {[
                  { label: 'Survey', value: 0 },
                  { label: 'Campaign', value: 1 },
                ]?.map((d) => (
                  <option key={d?.value} value={d?.value}>
                    {d?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Redirect Info Modal */}
      <RedirectInfo
        open={redirectInfoModal}
        onClose={() => setRedirectInfoModal(false)}
        surveyData={surveyData}
        surveyId={surveyData?._id}
      />
    </div>
  );
};
export default Details;
